/**
 * Domínio Meta CTWA — janela de atribuição, rota Purchase e validação de clid.
 * Lookup de lead/conversa: metaAttributionLookup.ts
 */

/** Valida ctwa_clid antes de enviar à CAPI (espelha regras da edge meta-conversions). */
export function sanitizeCtwaClid(value: unknown): string | null {
    if (typeof value !== 'string') return null;
    let trimmed = value.trim();

    if (trimmed.length > 160 && /^[A-Za-z0-9+/_-]+=*$/.test(trimmed)) {
        try {
            const padded = trimmed.replace(/-/g, '+').replace(/_/g, '/');
            const padLen = (4 - (padded.length % 4)) % 4;
            const decoded = atob(padded + '='.repeat(padLen)).trim();
            if (decoded.length >= 10 && decoded.length <= 256 && /^[A-Za-z0-9._-]+$/.test(decoded)) {
                trimmed = decoded;
            }
        } catch {
            // mantém trimmed original
        }
    }

    if (trimmed.length < 10 || trimmed.length > 256) return null;
    const lower = trimmed.toLowerCase();
    if (['meta_ads', 'fb_ads', 'undefined', 'null'].includes(lower)) return null;
    if (/^https?:\/\//i.test(trimmed) || /^[\[{]/.test(trimmed)) return null;
    if (!/^[A-Za-z0-9._-]+$/.test(trimmed)) return null;
    if (trimmed.length > 120 && /[+/=]/.test(trimmed)) return null;
    return trimmed;
}

export interface MetaAttribution {
    ctwa_clid: string | null;
    source_id: string | null;
    dataset_id: string | null;
    pixel_id: string | null;
    page_id: string | null;
    waba_id: string | null;
    ad_id: string | null;
    ad_name: string | null;
    adset_id: string | null;
    adset_name: string | null;
    campaign_id: string | null;
    campaign_name: string | null;
    captured_at?: string;
    ig_sid?: string | null;
    instagram_business_account_id?: string | null;
    conversion_sent?: boolean;
    conversion_sent_at?: string;
    conversion_value?: number;
    conversion_attributed?: boolean;
    conversion_offline_sent?: boolean;
    conversion_offline_sent_at?: string;
    /** Marcos de funil já enviados à CAPI: stageId → meta */
    stage_conversions_sent?: Record<
        string,
        { event: string; at: string; event_id: string }
    >;
}

export const CTWA_PURCHASE_ATTRIBUTION_DAYS = 7;

export function isCtwaClidWithinPurchaseWindow(
    capturedAt: string | null | undefined,
    nowMs: number = Date.now()
): boolean {
    if (!capturedAt) return false;
    const capturedMs = new Date(capturedAt).getTime();
    if (Number.isNaN(capturedMs)) return false;
    const ageMs = nowMs - capturedMs;
    return ageMs >= 0 && ageMs <= CTWA_PURCHASE_ATTRIBUTION_DAYS * 24 * 60 * 60 * 1000;
}

export function resolveClickTimeForPurchaseWindow(params: {
    capturedAt?: string | null;
    firstTouchAt?: string | null;
    asOfMs?: number;
}): string | null {
    const asOf = params.asOfMs ?? Date.now();
    const candidates = [params.capturedAt, params.firstTouchAt]
        .filter((t): t is string => Boolean(t))
        .map((iso) => ({ iso, ms: new Date(iso).getTime() }))
        .filter((c) => !Number.isNaN(c.ms) && c.ms <= asOf);
    if (!candidates.length) return null;
    candidates.sort((a, b) => b.ms - a.ms);
    return candidates[0].iso;
}

export function isPhysicalStoreOrigin(origin: string | null | undefined): boolean {
    if (!origin) return false;
    const normalized = origin
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase();
    return /loja|visita|presencial|fisic|balcao|showroom/.test(normalized);
}

export function hasMetaAdImpact(params: {
    ctwaClid?: string | null;
    igSid?: string | null;
    firstTouchSource?: string | null;
}): boolean {
    return Boolean(params.ctwaClid) || Boolean(params.igSid) || params.firstTouchSource === 'meta_ads';
}

export type MetaPurchaseRoute = 'ctwa' | 'offline' | 'skip';

export function resolveMetaPurchaseRoute(params: {
    ctwaClid?: string | null;
    igSid?: string | null;
    capturedAt?: string | null;
    firstTouchAt?: string | null;
    firstTouchSource?: string | null;
    origin?: string | null;
    asOfMs?: number;
    nowMs?: number;
}): MetaPurchaseRoute {
    const ctwaClid = params.ctwaClid || null;
    const impacted = hasMetaAdImpact({
        ctwaClid,
        igSid: params.igSid,
        firstTouchSource: params.firstTouchSource,
    });
    if (!impacted) return 'skip';

    if (params.igSid) return 'ctwa';

    const asOfMs = params.asOfMs ?? params.nowMs ?? Date.now();
    const clickAt = resolveClickTimeForPurchaseWindow({
        capturedAt: params.capturedAt,
        firstTouchAt: params.firstTouchAt,
        asOfMs,
    });
    const fresh = isCtwaClidWithinPurchaseWindow(clickAt, asOfMs);
    const staleClid = Boolean(ctwaClid) && Boolean(clickAt) && !fresh;

    if (ctwaClid && fresh) return 'ctwa';
    if (staleClid) return 'offline';
    if (ctwaClid && !clickAt) return 'ctwa';
    if (isPhysicalStoreOrigin(params.origin)) return 'offline';
    return 'skip';
}

export type CapPurchaseResult = {
    success: boolean;
    error?: string;
    events_received?: number;
    attributed?: boolean;
};

/** Interpreta resposta CAPI de Purchase CTWA (exige attributed=true). */
export function evaluateCtwaPurchaseResult(result: CapPurchaseResult): CapPurchaseResult {
    const eventsReceived = result.events_received ?? 0;
    if (result.success && eventsReceived > 0 && result.attributed === true) {
        return { ...result, attributed: true };
    }
    if (result.success && eventsReceived > 0) {
        return { success: false, error: 'Purchase enviado sem atribuição CTWA ao anúncio', attributed: false };
    }
    if (result.success && eventsReceived < 1) {
        return { success: false, error: 'Meta não contabilizou o Purchase' };
    }
    return result;
}
